<template>
  <div class="relative">
    <button
      class="flex w-full items-center justify-between gap-2 rounded-lg border-2 border-black-dark bg-white px-3 py-2 text-sm font-bold text-gray-800 shadow-yellow-hard-sm transition-all hover:bg-kekkle-yellow/20 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none dark:bg-kekkle-dark dark:text-slate-200"
      @click="toggleDropdown"
    >
      <div class="flex items-center gap-2">
        <span class="text-base">{{ currentLanguage?.flag }}</span>
        <span>{{ currentLanguage?.name }}</span>
      </div>
      <span
        class="text-sm transition-transform"
        :class="isOpen ? 'rotate-180' : ''"
      >
        ▼
      </span>
    </button>

    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="absolute right-0 top-full z-50 mt-2 w-full min-w-[180px] overflow-hidden rounded-lg border-2 border-black-dark bg-white shadow-yellow-hard dark:bg-kekkle-dark"
      >
        <button
          v-for="language in availableLanguages"
          :key="language.code"
          class="flex w-full items-center gap-2 border-b-2 border-black-dark px-3 py-2 text-left text-sm font-medium transition-all last:border-b-0 hover:bg-kekkle-yellow/20 active:translate-x-[1px] active:translate-y-[1px] dark:hover:bg-kekkle-yellow/10"
          :class="
            language.code === currentLanguageCode
              ? 'bg-kekkle-yellow/30 dark:bg-kekkle-yellow/20'
              : ''
          "
          @click="selectLanguage(language.code)"
        >
          <span class="text-base">{{ language.flag }}</span>
          <span class="flex-1 text-gray-800 dark:text-slate-200">{{
            language.name
          }}</span>
          <span
            v-if="language.code === currentLanguageCode"
            class="text-sm text-kekkle-yellow-dark dark:text-kekkle-yellow"
          >
            ✓
          </span>
        </button>
      </div>
    </Transition>

    <!-- Backdrop -->
    <Transition name="backdrop">
      <div v-if="isOpen" class="fixed inset-0 z-40" @click="closeDropdown" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useLanguages } from "@/composables/useLanguages";

const { availableLanguages, currentLanguageCode, setLanguage } = useLanguages();

const isOpen = ref(false);

const currentLanguage = computed(() => {
  return (
    availableLanguages.value.find(
      (lang) => lang.code === currentLanguageCode.value,
    ) || availableLanguages.value[0]
  );
});

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function closeDropdown() {
  isOpen.value = false;
}

function selectLanguage(code: string) {
  setLanguage(code);
  closeDropdown();
}

// Close dropdown on escape key
function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && isOpen.value) {
    closeDropdown();
  }
}

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}
</style>
